import { ChangeDetectionStrategy, Component, effect, inject, input, output } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { FormControlsComponent, InputClearButtonDirective } from 'eurogard-material-form-system';
import { FormSubmitMethod, RXAP_FORM_DEFINITION, RxapFormsModule } from 'eurogard-forms';
import { FormComponentProviders, FormProviders } from './form.providers';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { TimeslotControlComponent } from 'angular-controls/timeslot-control/timeslot-control.component';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { AlarmGroupForm, IAlarmGroupForm } from 'angular-forms/alarm-group-form/alarm-group.form';
import { TimeslotsDto } from 'open-api-service-app-settings/components/timeslots-dto';
import { ConfirmDialogService } from 'angular-components/confirm-dialog/confirm-dialog.service';
import { AlarmGroupDtoResponse } from 'open-api-service-app-settings/responses/alarm-group-dto.response';

@Component({
  standalone: true,
  selector: 'eurogard-alarm-group-form',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './alarm-group-form.component.html',
  styleUrls: [ './alarm-group-form.component.scss' ],
  imports: [
    ReactiveFormsModule,
    FormControlsComponent,
    RxapFormsModule,
    MatSlideToggleModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
    MatButtonModule,
    InputClearButtonDirective,
    TimeslotControlComponent,
  ],
  providers: [ FormProviders, FormComponentProviders ],
})
export class AlarmGroupFormComponent {

  daysOfTheWeek = [
    { controlName: 'monday', localizedName: $localize`Monday` },
    { controlName: 'tuesday', localizedName: $localize`Tuesday` },
    { controlName: 'wednesday', localizedName: $localize`Wednesday` },
    { controlName: 'thursday', localizedName: $localize`Thursday` },
    { controlName: 'friday', localizedName: $localize`Friday` },
    { controlName: 'saturday', localizedName: $localize`Saturday` },
    { controlName: 'sunday', localizedName: $localize`Sunday` },
  ]

  readOnly = input<boolean>(false);

  hideActiveToggle = input<boolean>(false);
  enforceCompactDesign = input<boolean>(false);
  showOnlyTimeslots = input<boolean>(false);

  submitMethod = input<FormSubmitMethod<any> | null>(null);

  initial = input<IAlarmGroupForm>();

  form = inject<AlarmGroupForm>(RXAP_FORM_DEFINITION);

  defaultConfiguration = input<AlarmGroupDtoResponse>();

  private readonly confirmDialogService = inject(ConfirmDialogService);

  protected initialized = false;

  constructor() {
    effect(() => {
      const initial = this.initial();
      if (initial) {
        this.form.rxapFormGroup.patchValue(initial);
        this.initialized = true;
      }
    });
    effect(() => {
      if (this.readOnly()) {
        this.form.rxapFormGroup.disable();
      } else {
        this.form.rxapFormGroup.enable();
      }
    })
  }

  addDayToTimeslots($event: MatSelectChange) {
    this.form.timeslots[$event.value as keyof TimeslotsDto].setValue([
      {from: '00:00', to: '24:00'},
    ]);
    $event.source.value = null;
  }

  copyTimeFrame(copyDayIndex: number, pasteDaysIndexes: boolean[]) {
    const copyDay = this.daysOfTheWeek[copyDayIndex].controlName;
    for (const [pasteDayIndex, pasteDaySelected] of pasteDaysIndexes.entries()) {
      const pasteDay = this.daysOfTheWeek[pasteDayIndex].controlName;
      if (pasteDaySelected) {
        this.form.timeslots[pasteDay as keyof TimeslotsDto].setValue(
          this.form.timeslots[copyDay as keyof TimeslotsDto].value
        );
      }
    }
  }

  protected importDefaultConfiguration() {
    this.confirmDialogService.confirm(
      $localize`Importing the default configuration will override your current progress. However it WON'T submit the configuration autmatically.`,
      undefined,
      $localize`Import Default Configuration`,
    ).then((confirmed) => {
      if (confirmed) {
        this.form.rxapFormGroup.patchValue({
          timeslots: this.defaultConfiguration()?.timeslots,
        });
      }
    });
  }

  protected manualReset() {
    const initial = this.initial();
    if (initial) {
      this.form.rxapFormGroup.patchValue(initial);
      this.initialized = true;
    }
  }
}
